import { User, UserList } from "@phosphor-icons/react";
import { useState } from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";

import WorkspaceAccountSelectorItem from "./WorkspaceAccountSelectorItem";

interface WorkspaceAccountSelectorProps<Selection extends string[] | string> {
  selection: Selection;
  setSelection: (selection: Selection) => void;
  isNavigatorOnly?: boolean;
  className?: string;
  size?: "lg" | "md" | "sm";
}

export default function WorkspaceAccountSelector<
  Selection extends string[] | string,
>({
  selection,
  setSelection,
  isNavigatorOnly = false,
  className = null,
  size = "md",
}: WorkspaceAccountSelectorProps<Selection>) {
  const [searchInputValue, setSearchInputValue] = useState("");
  const { accounts: workspaceAccounts } = useSelectedWorkspaceContext();

  let accounts = workspaceAccounts;
  if (isNavigatorOnly) {
    accounts = workspaceAccounts.filter(
      ({ sales_navigator }) => sales_navigator,
    );
  }

  const filteredAccounts = accounts.filter(({ full_name }) =>
    full_name.toLowerCase().includes(searchInputValue.toLowerCase()),
  );

  const isMultiSelect = Array.isArray(selection);

  function removeAccountId(accountId: string) {
    if (isMultiSelect) {
      setSelection(selection.filter((id) => id !== accountId) as Selection);
    }
  }
  function addAccountId(accountId: string) {
    setSelection([...selection, accountId] as Selection);
  }
  let triggerLeftIcon = isMultiSelect ? <UserList /> : <User />;
  let triggerLabel = isMultiSelect ? "All accounts" : "Select account";

  if (
    isMultiSelect
      ? selection.length && selection.length !== accounts.length
      : !!selection
  ) {
    triggerLeftIcon = (
      <div className="flex shrink-0 flex-row -space-x-1.5 rtl:space-x-reverse">
        {(typeof selection === "string"
          ? [selection]
          : selection.slice(0, 3)
        ).map((selectedAccountId) => {
          const account = accounts.find(({ id }) => id === selectedAccountId);

          return (
            <ProfileImage
              key={account.id}
              size="xs"
              className="ring-1 ring-purple-50"
              src={account.picture_url}
            />
          );
        })}
      </div>
    );

    triggerLabel =
      isMultiSelect && selection.length > 1
        ? `${selection.length} Accounts`
        : accounts.find(
            ({ id }) => id === (isMultiSelect ? selection[0] : selection),
          ).full_name;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <FilterButton
          leftIcon={triggerLeftIcon}
          className={className}
          size={size}
        >
          {triggerLabel}
        </FilterButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="min-w-[var(--radix-popper-anchor-width)] w-60"
        align="end"
      >
        {workspaceAccounts.length > 5 && (
          <MenuSearchInput
            placeholder="Search accounts"
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
          />
        )}

        <DropdownMenuList>
          {isMultiSelect && (
            <DropdownMenuItem onSelect={() => setSelection([] as Selection)}>
              All accounts
            </DropdownMenuItem>
          )}

          {filteredAccounts.map((account) => (
            <WorkspaceAccountSelectorItem
              key={account.id}
              account={account}
              isMultiSelect={isMultiSelect}
              selection={selection}
              setSelection={setSelection}
              onRemoveAccountId={removeAccountId}
              onAddAccountId={addAccountId}
            />
          ))}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
